import { useEffect, useState } from "react";

// Animated ALIX promo reel. Characters step forward one-by-one (left→right),
// each with a caption of what that rep does on the site; ends on a
// "misteck.com · JOIN NOW" card. Pass `qr` (data URL) + `link` for the game
// version to show a clickable QR at the end. Reusable on landing + in-game.

const LINEUP = "https://customer-assets-rejwkqb3.emergentagent.net/job_7d98ce7e-973b-4a5e-8e79-7802942e70f9/artifacts/y2c71e83_mist_image_3.png";

const REPS = [
  { name: "CREATE", line: "Generate images, music & videos with ALIX" },
  { name: "EDIT", line: "Trim, stylize & clean up your clips — free" },
  { name: "PUBLISH", line: "Post reels & grow on the Mist feed" },
  { name: "PLAY", line: "Games that earn gems & unlock creator tools" },
  { name: "VAULT", line: "Store & organize everything, securely" },
];

export default function AlixAdReel({ qr, link, onJoin, className = "" }) {
  const N = REPS.length;
  const [step, setStep] = useState(0); // 0..N-1 = characters, N = end card

  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s >= N ? 0 : s + 1)), 2600);
    return () => clearInterval(t);
  }, [N]);

  const end = step >= N;

  return (
    <div data-testid="alix-ad-reel" className={`relative w-full overflow-hidden rounded-2xl border-2 border-gold/50 bg-[#0b1020] ${className}`}>
      {/* Character stage */}
      <div className="relative flex h-56 items-end justify-center gap-0.5 px-2 pt-3">
        {REPS.map((rep, i) => {
          const active = !end && i === step;
          return (
            <div key={rep.name} data-testid={`alix-rep-${i}`}
              className="relative h-full flex-1 overflow-hidden rounded-lg transition-all duration-500 ease-out"
              style={{
                backgroundImage: `url(${LINEUP})`,
                backgroundSize: "500% 100%",
                backgroundPosition: `${(i / (N - 1)) * 100}% center`,
                transform: active ? "translateY(-10%) scale(1.12)" : "translateY(0) scale(0.94)",
                opacity: end ? 0.25 : active ? 1 : 0.45,
                zIndex: active ? 10 : 1,
                filter: active ? "none" : "grayscale(0.4) brightness(0.7)",
              }}
            />
          );
        })}
      </div>

      {/* Caption / end card */}
      <div className="min-h-[92px] border-t-2 border-white/10 bg-black/50 px-4 py-3 text-center">
        {!end ? (
          <div key={step} className="animate-[fadeIn_0.4s_ease]">
            <p className="font-mono text-[11px] font-black uppercase tracking-[0.24em] text-gold">{REPS[step].name}</p>
            <p className="mt-1 font-serif text-lg leading-tight text-white">{REPS[step].line}</p>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4">
            <div className="text-left">
              <p className="font-serif text-2xl leading-none text-white">misteck.com</p>
              <button onClick={onJoin} data-testid="alix-ad-join"
                className="mt-2 rounded-full border-2 border-gold bg-gold px-5 py-2 font-mono text-[12px] font-black uppercase tracking-widest text-black active:scale-95">
                Join now →
              </button>
            </div>
            {qr && (
              <a href={link || "#"} target="_blank" rel="noreferrer" data-testid="alix-ad-qr"
                className="block rounded-lg border-2 border-gold/50 bg-white p-1.5">
                <img src={qr} alt="Scan to join" className="h-20 w-20" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* progress dots */}
      <div className="absolute right-2 top-2 flex gap-1">
        {[...REPS, "end"].map((_, i) => (
          <span key={i} className={`h-1.5 w-1.5 rounded-full ${(end ? N : step) === i ? "bg-gold" : "bg-white/30"}`} />
        ))}
      </div>
    </div>
  );
}
